import { batch, computed, signal, Signal } from '@preact/signals-react';
import { injectable } from 'inversify';

import { Success } from '@vp/common/core/OperationResult';
import { ViewModel, ViewModelDispose } from '@vp/common/ui/ViewModel';
import { sleep } from '@vp/common/util';
import { AvatarManagerPort } from '@vp/manager/avatar/core/interfaces/AvatarManagerPort';
import { ProfileManagerPort } from '@vp/manager/profile/core/interface/ProfileManagerPort';
import { AppNotificationService } from '@vp/notification/AppNotificationService';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';
import { ProfilePhotoLiveStatus, ProfilePhotoType } from '@vp/profile/core/model/ProfilePhotoModel';

enum AvatarOperation {
  Upload = 'upload',
  Remove = 'remove',
}

@injectable()
export class AvatarManagerViewModel extends ViewModel implements ViewModelDispose {
  readonly profile: Signal<ProfileModel | null> = this.profileManagerPort.active;

  readonly showImage: Signal<boolean> = computed(() => !!this.profile.value?.image.url);

  readonly deleteDialogShown: Signal<boolean> = signal(false);
  readonly animateDialogShown: Signal<boolean> = signal(false);
  readonly makeStaticDialogShown: Signal<boolean> = signal(false);

  readonly removing: Signal<boolean> = signal(false);
  readonly uploading: Signal<boolean> = signal(false);
  readonly animating: Signal<boolean> = signal(false);
  readonly makingStatic: Signal<boolean> = signal(false);

  /**
   * Controls whether the "Animate" button should be disabled.
   *
   * Rules:
   * - Disabled if the photo is in Pending state (animation in progress).
   * - Disabled if there are no animation attempts left AND:
   *    - the photo is not Live, AND
   *    - there is no previously saved animation (liveStatus !== Ready).
   * - Enabled if the photo has a saved animation (Ready), even if its type is Static,
   *   since re-animating does not consume additional attempts.
   */
  readonly animateButtonDisabled: Signal<boolean> = computed(() => {
    const image = this.profile.value!.image;

    const isPending = image.liveStatus === ProfilePhotoLiveStatus.Pending;
    const isLive = image.liveStatus === ProfilePhotoLiveStatus.Ready && image.type === ProfilePhotoType.Live;

    const hasSavedAnimation = image.liveStatus === ProfilePhotoLiveStatus.Ready;
    const noAttemptsLeft = !this.avatarManagerPort.hasProfileImageAnimationAttempts();

    return isPending || (noAttemptsLeft && !isLive && !hasSavedAnimation);
  });

  readonly animateButtonLoading: Signal<boolean> = computed(() => {
    const photo = this.profile.value!.image;
    return this.animating.value || this.makingStatic.value || photo.liveStatus === ProfilePhotoLiveStatus.Pending;
  });

  readonly animateButtonText: Signal<string> = computed(() => {
    return this.isActiveLivePhoto() ? 'Вернуть' : `Анимировать`;
  });

  readonly animateDialogText: Signal<string> = computed(() => {
    const [attemptsLeft, total] = this.getAnimationQuota();
    return `У вас есть ограниченное количество анимаций для фотографии профиля (${attemptsLeft} из ${total}). После запуска процесс займёт немного времени, поэтому результат появится не сразу — обновите страницу позже, и фото профиля станет анимированным. В любой момент вы можете отменить анимацию, и изображение вернётся к исходному виду.`;
  });

  private controller?: AbortController;

  constructor(
    private readonly profileManagerPort: ProfileManagerPort,
    private readonly notificationService: AppNotificationService,
    private readonly avatarManagerPort: AvatarManagerPort,
  ) {
    super();
  }

  dispose(): void {
    this.controller?.abort();
  }

  onAnimateButtonClick = (): void => {
    if (this.isActiveLivePhoto()) {
      this.showMakeStaticDialog();
    } else {
      this.showAnimateDialog();
    }
  };

  onFileUpload = async (files: File[]): Promise<void> => {
    this.uploading.value = true;
    await this.handleUpload(files[0]);
    this.uploading.value = false;
  };

  onRemoveButtonClick = (): void => {
    this.deleteDialogShown.value = true;
  };

  removeImage = async (): Promise<void> => {
    this.removing.value = true;
    await this.handleRemove();

    batch(() => {
      this.removing.value = false;
      this.deleteDialogShown.value = false;
    });
  };

  closeDeleteDialog = (): void => {
    this.deleteDialogShown.value = false;
  };

  showAnimateDialog = (): void => {
    this.animateDialogShown.value = true;
  };

  closeAnimateDialog = (): void => {
    this.animateDialogShown.value = false;
  };

  showMakeStaticDialog = (): void => {
    this.makeStaticDialogShown.value = true;
  };

  closeMakeStaticDialog = (): void => {
    this.makeStaticDialogShown.value = false;
  };

  animate = async (): Promise<void> => {
    this.animating.value = true;
    await this.handleAnimate();
    this.animating.value = false;
  };

  makeStatic = async (): Promise<void> => {
    this.makingStatic.value = true;
    await this.handleMakeStatic();
    this.makingStatic.value = false;
  };

  private async handleMakeStatic(): Promise<void> {
    this.controller = new AbortController();
    const result = await this.avatarManagerPort.makeStatic(this.controller);
    await sleep(250);

    if (result instanceof Success) {
      this.showMakeStaticSuccessMessage();
      this.closeMakeStaticDialog();
    } else {
      this.showMakeStaticErrorMessage(result.message);
    }
  }

  private async handleAnimate(): Promise<void> {
    this.controller = new AbortController();
    const result = await this.avatarManagerPort.makeLive(this.controller);
    await sleep(250);

    if (result instanceof Success) {
      this.showAnimateSuccessMessage();
      this.closeAnimateDialog();
    } else {
      this.showAnimateErrorMessage(result.message);
    }
  }

  private async handleRemove(): Promise<void> {
    this.controller = new AbortController();
    const success = await this.avatarManagerPort.remove(this.profile.value!.id, this.profile.value!.image.id, this.controller);
    await sleep(250);

    if (success) {
      this.showSuccessNotification(AvatarOperation.Remove);
    } else {
      this.showErrorNotification(AvatarOperation.Remove);
    }
  }

  private async handleUpload(avatarFile: File): Promise<void> {
    this.controller = new AbortController();
    const success = await this.avatarManagerPort.upload(this.profile.value!.id, avatarFile, this.controller);
    await sleep(250);

    if (success) {
      this.showSuccessNotification(AvatarOperation.Upload);
    } else {
      this.showErrorNotification(AvatarOperation.Upload);
    }
  }

  private showSuccessNotification(operation: AvatarOperation): void {
    this.notificationService.enqueue({
      variant: 'success',
      message: `Фото было ${operation === AvatarOperation.Upload ? 'загружено' : 'удалено'}`,
    });
  }

  private showErrorNotification(operation: AvatarOperation): void {
    this.notificationService.enqueue({
      variant: 'error',
      message: 'Что-то пошло не так',
      secondaryMessage: `Не удалось ${operation === AvatarOperation.Upload ? 'загрузить' : 'удалить'} фото`,
    });
  }

  private showAnimateSuccessMessage(): void {
    const message = 'Анимирование фотографии профиля было успешно запланировано';
    this.notificationService.enqueue({ variant: 'success', message });
  }

  private showMakeStaticSuccessMessage(): void {
    const message = 'Анимация отменена. Фото профиля стало оригинальным';
    this.notificationService.enqueue({ variant: 'success', message });
  }

  private showAnimateErrorMessage(message: string): void {
    const secondaryMessage = message || 'Не удалось анимировать изображение профиля';
    this.notificationService.enqueue({ variant: 'error', message: 'Что-то пошло не так', secondaryMessage });
  }

  private showMakeStaticErrorMessage(message: string): void {
    const secondaryMessage = message || 'Не удалось отменить анимацию изображения профиля';
    this.notificationService.enqueue({ variant: 'error', message: 'Что-то пошло не так', secondaryMessage });
  }

  private getAnimationQuota(): [number, number] {
    const active = this.profileManagerPort.active.value!;
    return active.liveMedia.getProfileImageQuota();
  }

  private isActiveLivePhoto(): boolean {
    const photo = this.profile.value!.image;
    return photo.liveStatus === ProfilePhotoLiveStatus.Ready && photo.type === ProfilePhotoType.Live;
  }
}
